import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { cx } from '@emotion/css';
import * as models from 'src/models';
import * as localization from 'src/localization';
import * as cssClass from 'src/lib/cssClass';
import useLocalized from 'src/hooks/useLocalized';
import Divider from 'src/components/Divider';
import Row from 'src/components/Row';
import Text from 'src/components/Text';
import Anchor from 'src/components/Anchor';
import Navbar from 'src/components/Navbar';
import Heading from 'src/components/Heading';
import Breadcrumbs from 'src/components/Breadcrumbs';
import HelpCreditCard from 'src/components/HelpCreditCard';
import HelpInstitution from 'src/components/HelpInstitution';
import InstitutionDetails from 'src/components/InstitutionDetails';
import InstitutionRelatedArticles from 'src/components/InstitutionRelatedArticles';

export type Props = {
  institution: models.institution.types.Institution;
  institutionSeed: models.institutionSeed.types.InstitutionSeed | null;
};

const InstitutionDashboard: React.FC<Props> = ({ institution, institutionSeed }) => {
  const localized = useLocalized(localization.views.InstitutionDashboard.create(institution));

  return (
    <>
      <Head>
        <title>{localized.title}</title>
      </Head>
      <Navbar className={cssClass.mb('20px')}>
        <Breadcrumbs>
          <Breadcrumbs.Crumb>
            <Link href="/">
              <Anchor>
                <Text>{localized.breadcrumbs.institutions}</Text>
              </Anchor>
            </Link>
          </Breadcrumbs.Crumb>
          <Breadcrumbs.Crumb>
            <Text color="grey60">{localized.breadcrumbs.current}</Text>
          </Breadcrumbs.Crumb>
        </Breadcrumbs>
      </Navbar>
      <Row responsive={{ at: 'md', reverse: true }}>
        <Row.Column size={2}>
          <Heading level={1} className={cssClass.mb('10px')}>
            {localized.title}
          </Heading>
          <Divider />
          <Heading level={4} className={cx(cssClass.mt('20px'), cssClass.mb('15px'))}>
            {localized.details.title}
          </Heading>
          <InstitutionDetails institution={institution} />
          <Divider visibility="hidden" />
          {institutionSeed && (
            <>
              <Heading level={4} className={cx(cssClass.mt('20px'), cssClass.mb('15px'))}>
                {localized.relatedArticles.title}
              </Heading>
              <InstitutionRelatedArticles institution={institution} institutionSeed={institutionSeed} />
            </>
          )}
        </Row.Column>
        <Row.Column size={1}>
          <HelpInstitution />
          <Divider visibility="hidden" />
          <HelpCreditCard />
          <Divider visibility="hidden" />
        </Row.Column>
      </Row>
    </>
  );
};

export default InstitutionDashboard;
